import { Link } from '@tanstack/react-router'
import { useState } from 'react'
import { EMPTY_FUNNEL, signupAwaitingPayment, useSignIn, writeFunnel } from '@lb/domain'
import { useTranslation } from '@lb/i18n'
import { Button, IconArrowForward, IconChevronLeft, Logo } from '@lb/ui'

import { env } from '../../env'
import { auth, http } from '../../lib/client'

/**
 * Sign in — the "already a member" screen.
 *
 * ── Focused, and branded (3 Oct 2026) ────────────────────────────────────
 * Still no progress steps, language header or topbar clutter: the logo, a
 * "Back to plans" affordance, the greeting, the two fields, "Forgot your
 * password?", one primary CTA and the way to sign up. The approved mock had
 * no logo at all; after launch it read as a bare, unbranded page to the
 * members arriving from the website's « Se connecter », so the client asked
 * for the logo and more of the brand. On wide screens a photo of Krysta and
 * Sarah sits beside the form; on phones the form comes first and the photo is dropped.
 *
 * ── Former WordPress members ─────────────────────────────────────────────
 * Their accounts and passwords moved over, but the most common failure on
 * launch night was a wrong password (13). The note under the form says to
 * use the usual email and password first, then reset if it is forgotten.
 *
 * ── One generic error message, deliberately ──────────────────────────────
 * The backend refuses sign-in on a single code (INVALID_CREDENTIALS) for
 * both "unknown email" and "wrong password" — telling somebody which is
 * wrong is an account-enumeration oracle. The form respects that: one
 * message, at the top of the card, no per-field flag.
 *
 * ── "Keep me signed in" — removed to match the mock ──────────────────────
 * The mock has no checkbox. Behaviour defaults to persistent (the
 * previous checkbox's `true` state), which matches everybody's actual
 * expectation on their own device. On a shared device, sign-out is the
 * appropriate lever — not a checkbox somebody has to remember to untick.
 */
export function LoginScreen({
  onSignedIn,
  onSignUp,
  onResumeSignup,
}: {
  onSignedIn: () => void
  onSignUp: () => void
  /** An unpaid signup signing in: its checkout is saved, open the pay step. */
  onResumeSignup: () => void
}) {
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const signIn = useSignIn(http, auth)

  return (
    <div className="flex min-h-dvh flex-col bg-canvas">
      <div className="mx-auto flex w-full max-w-[1080px] items-center justify-between gap-4 px-5 pt-6">
        <a href={env.VITE_WEBSITE_URL} aria-label={t('brand.name')} className="text-ink">
          <Logo size={30} />
        </a>
        <button
          type="button"
          onClick={onSignUp}
          className="inline-flex items-center gap-1.5 text-[13.5px] font-semibold text-ink-soft hover:text-ink"
        >
          <IconChevronLeft size={16} title={null} />
          {t('login.backToPlans')}
        </button>
      </div>

      <main
        id="main"
        className="mx-auto grid w-full max-w-[1080px] flex-1 items-center gap-10 px-5 py-10 lg:grid-cols-[minmax(0,480px)_minmax(0,1fr)] lg:gap-16 lg:py-14"
      >
        <div className="mx-auto w-full max-w-[480px] lg:mx-0">
        <h1 className="text-balance text-center font-serif text-[38px] font-bold leading-[1.05] text-ink md:text-[44px] lg:text-start">
          {t('login.title')}
        </h1>
        <p className="mx-auto mt-3.5 max-w-[440px] text-center text-[15.5px] text-slate lg:mx-0 lg:text-start">
          {t('login.subtitle')}
        </p>

        <div className="mt-8 rounded-[var(--r-lg)] border border-border bg-surface p-card">
          <form
            onSubmit={(event) => {
              event.preventDefault()
              signIn.mutate(
                { email: email.trim(), password, persist: true },
                {
                  onSuccess: onSignedIn,
                  /*
                   * Signed up, never paid: the account exists but cannot sign
                   * in. With the right password the API names the unpaid
                   * checkout, which the funnel reopens on the pay step — the
                   * same resume a refresh mid-signup uses.
                   */
                  onError: (error) => {
                    const checkoutId = signupAwaitingPayment(error)
                    if (!checkoutId) return
                    writeFunnel({ ...EMPTY_FUNNEL, checkoutId, email: email.trim() })
                    onResumeSignup()
                  },
                },
              )
            }}
            className="flex flex-col gap-5"
          >
            <label className="block">
              <span className="mb-1.5 block text-[13px] font-semibold text-ink">
                {t('login.emailLabel')}
              </span>
              <input
                className="w-full min-h-[48px] rounded-[var(--r)] border border-border bg-surface px-4 py-3 text-[15px] text-ink outline-none focus:border-ink focus:shadow-[0_0_0_3px_rgb(41_51_57/0.12)]"
                type="email"
                autoComplete="email"
                placeholder={t('login.emailPlaceholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </label>

            <label className="block">
              <span className="mb-1.5 block text-[13px] font-semibold text-ink">
                {t('login.passwordLabel')}
              </span>
              {/*
               * `Show` is an inline button on the right rather than a
               * separate row. Same pattern the mock uses; matches every
               * password-with-visibility-toggle a returning member has
               * seen elsewhere.
               */}
              <div className="relative">
                <input
                  className="w-full min-h-[48px] rounded-[var(--r)] border border-border bg-surface px-4 py-3 pe-16 text-[15px] text-ink outline-none focus:border-ink focus:shadow-[0_0_0_3px_rgb(41_51_57/0.12)]"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  placeholder={t('login.passwordPlaceholder')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-pressed={showPassword}
                  className="absolute inset-y-0 end-3 my-auto h-8 rounded-full px-2 text-[13px] font-semibold text-ink-soft hover:text-ink"
                >
                  {showPassword ? t('login.hide') : t('login.show')}
                </button>
              </div>
            </label>

            <Link
              to="/account/forgot-password"
              className="-mt-2 text-[13px] font-semibold text-ink underline underline-offset-2 hover:text-ink-soft"
            >
              {t('login.forgotPassword')}
            </Link>

            {signIn.isError && !signupAwaitingPayment(signIn.error) ? (
              <p
                role="alert"
                className="rounded-[var(--r)] bg-error-surface p-3 text-[13.5px] text-error"
              >
                {t('onboarding.badCredentials')}
              </p>
            ) : null}

            <Button
              type="submit"
              size="lg"
              loading={signIn.isPending}
              loadingLabel={t('state.busy')}
            >
              {t('login.submit')}
              <IconArrowForward size={16} title={null} />
            </Button>

            <button
              type="button"
              onClick={onSignUp}
              className="mt-1 text-center text-[13.5px] text-muted hover:text-ink"
            >
              {t('login.noAccount')}
            </button>
          </form>
        </div>

        {/* Former WordPress members: same email and password first, reset only if forgotten. */}
        <p className="mt-4 rounded-[var(--r)] bg-cream-dark px-4 py-3.5 text-[13.5px] leading-[1.5] text-ink-soft">
          {t('login.formerMember')}{' '}
          {t('login.formerMemberForgot')}{' '}
          <Link
            to="/account/forgot-password"
            className="whitespace-nowrap font-semibold text-ink underline underline-offset-2 hover:text-ink-soft"
          >
            {t('login.forgotPassword')}
          </Link>
        </p>
        </div>

        {/* The brand, beside the form on wide screens; dropped on phones so the form comes first. */}
        {/* Krysta and Sarah (client's photo, 3 Oct 2026), at the photo's own 1067×854 so nobody is cropped. */}
        <div aria-hidden className="relative hidden aspect-[1067/854] w-full self-center lg:block">
          <img
            src="/brand/photos/krysta-sarah-laptop.webp"
            alt=""
            className="absolute inset-0 h-full w-full rounded-[var(--r-xl)] object-cover"
          />
          <div className="absolute bottom-6 start-6 inline-flex items-center gap-2.5 rounded-full bg-surface/90 px-4 py-2.5 text-ink shadow-sm backdrop-blur">
            <Logo size={22} withWordmark={false} />
            <span className="font-serif text-[15px] font-bold">{t('brand.name')}</span>
          </div>
        </div>
      </main>
    </div>
  )
}
